import { useParams } from 'react-router-dom';
import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../apiConfig";
import { motion, AnimatePresence } from 'framer-motion';
import TeamCard from "../../Components/Team/TeamCard";
import FixtureCard from "../../Components/UIcomponents/FixtureCard";
import Result from "../../Components/UIcomponents/Result";
import LeagueTable from "./components/LeagueTable";
import noImg from "../../images/noresult.png";
import bannerImg from "../../images/teamcover.png";
import { Calendar, Trophy } from 'lucide-react';

function PremierTeams() {
    const { id } = useParams();
    const [divisionName, setDivisionName] = useState('');
    const [teamsData, setTeamsData] = useState([]);
    const [fixturesData, setFixturesData] = useState([]);
    const [resultsData, setResultsData] = useState([]);
    const [activeTab, setActiveTab] = useState('TEAMS');
    const [loading, setLoading] = useState(true);

    const isMobile = window.innerWidth <= 768;

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/currentDivision`).then((res) => {
            const current = res.data.find(d => d.PCID.toString() === id.toString());
            if (current) setDivisionName(current.name.toUpperCase());
        });

        axios.get(`${API_BASE_URL}/divisionTeam/${id}`).then((res) => {
            setTeamsData(res.data);
            setLoading(false);
        });

        axios.get(`${API_BASE_URL}/divisionFixture/${id}`).then((res) => {
            setFixturesData(res.data);
        });

        axios.get(`${API_BASE_URL}/divisionResult/${id}`).then((res) => {
            setResultsData(res.data);
        });
    }, [id]);

    const styles = {
        banner: {
            width: '100%',
            maxWidth: '1232px',
            height: isMobile ? '160px' : '256px',
            background: `url(${bannerImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            margin: '32px auto 0 auto',
            opacity: 1,
        },
        bannerOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6))',
        },
        bannerTitle: {
            fontFamily: 'var(--fontAnton)',
            fontSize: isMobile ? '36px' : '64px',
            color: '#FFFFFF',
            fontWeight: 400,
            textTransform: 'uppercase',
            margin: 0,
            textAlign: 'center',
            zIndex: 2,
            letterSpacing: '4px',
            textShadow: '0 10px 30px rgba(0,0,0,0.8)',
        },
        container: {
            maxWidth: '1100px',
            margin: '0 auto',
            padding: isMobile ? '40px 16px' : '64px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: isMobile ? '32px' : '48px',
        },
        tabWrapper: {
            display: 'flex',
            justifyContent: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '12px' : '16px',
            width: '100%',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            overflowX: isMobile ? 'auto' : 'visible',
            padding: isMobile ? '4px 16px 16px 16px' : '0',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none'
            }
        },
        tabBtn: (isActive) => ({
            padding: isMobile ? '10px 18px' : '12px 40px',
            minWidth: isMobile ? 'auto' : '140px',
            whiteSpace: 'nowrap',
            background: isActive ? '#060D8A' : 'transparent',
            border: '2px solid #060D8A',
            color: isActive ? '#FFFFFF' : '#060D8A',
            fontFamily: 'var(--fontAnton)',
            fontWeight: 400,
            fontSize: isMobile ? '14px' : '15px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            borderRadius: '0px',
            flexShrink: 0,
        }),
        grid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(160px, 1fr))' : 'repeat(4, 1fr)',
            gap: isMobile ? '16px' : '24px',
            width: '100%',
        }
    };

    const MatchEmptyState = ({ type }) => {
        const isFixtures = type === 'fixtures';
        return (
            <div style={{
                gridColumn: '1/-1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? '40px 20px' : '60px 40px',
                background: 'rgba(6, 13, 138, 0.02)',
                border: '1px dashed rgba(6, 13, 138, 0.2)',
                borderRadius: '8px',
                textAlign: 'center',
                gap: '12px',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <div style={{ opacity: 0.15, marginBottom: '8px' }}>
                    {isFixtures ? <Calendar size={isMobile ? 48 : 64} color="#060D8A" /> : <Trophy size={isMobile ? 48 : 64} color="#060D8A" />}
                </div>
                <h3 style={{ 
                    fontFamily: 'var(--fontAnton)', 
                    fontSize: isMobile ? '18px' : '22px', 
                    color: '#060D8A', 
                    margin: 0, 
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                }}>
                    {isFixtures ? 'No Upcoming Fixtures' : 'No Recent Results Found'}
                </h3>
                <p style={{ 
                    fontFamily: 'var(--fontDisplay)', 
                    fontSize: isMobile ? '13px' : '15px', 
                    color: 'rgba(0,0,0,0.4)', 
                    margin: 0,
                    fontWeight: 600
                }}>
                    {isFixtures ? 'The schedule for this season is yet to be announced.' : 'Matches are still in progress or results are pending.'}
                </p>
            </div>
        );
    };

    const renderContent = () => {
        if (loading) return <div className="loader" style={{ margin: '50px auto' }}></div>;

        switch (activeTab) {
            case 'TEAMS':
                return (
                    <div style={styles.grid}>
                        {teamsData.length > 0 ? teamsData.map((team, i) => (
                            <TeamCard data={team} key={i} league={divisionName} />
                        )) : <div style={{ gridColumn: '1/-1', textAlign: 'center' }}><img src={noImg} alt="No Data" /></div>}
                    </div>
                );
            case 'MATCHES':
                return (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: isMobile ? '40px' : '80px' }}>
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <h4 style={{ fontFamily: 'var(--fontAnton)', color: '#060D8A', margin: 0, fontSize: '18px', textTransform: 'uppercase', opacity: 0.8 }}>Upcoming Fixtures</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '24px' }}>
                                {fixturesData.length > 0 ? (
                                    fixturesData.map((f, i) => <FixtureCard data={f} key={i} />)
                                ) : <MatchEmptyState type="fixtures" />}
                            </div>
                        </div>
                        
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <h4 style={{ fontFamily: 'var(--fontAnton)', color: '#060D8A', margin: 0, fontSize: '18px', textTransform: 'uppercase', opacity: 0.8 }}>Recent Results</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '24px' }}>
                                {resultsData.length > 0 ? (
                                    resultsData.map((r, i) => <Result data={r} key={i} />)
                                ) : <MatchEmptyState type="results" />}
                            </div>
                        </div>
                    </div>
                );
            case 'LEAGUE TABLE':
                return (
                    <div style={{ width: '100%', boxSizing: 'border-box' }}>
                        <LeagueTable id={id} />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            style={{ background: '#FFFFFF', minHeight: '100vh' }}
        >
            <div style={styles.banner}>
                <div style={styles.bannerOverlay}></div>
                <h1 style={styles.bannerTitle}>{divisionName}</h1>
            </div>

            <div style={styles.container}>
                <div style={styles.tabWrapper}>
                    <button style={styles.tabBtn(activeTab === 'TEAMS')} onClick={() => setActiveTab('TEAMS')}>Teams</button>
                    <button style={styles.tabBtn(activeTab === 'MATCHES')} onClick={() => setActiveTab('MATCHES')}>Matches</button>
                    <button style={styles.tabBtn(activeTab === 'LEAGUE TABLE')} onClick={() => setActiveTab('LEAGUE TABLE')}>League Table</button>
                </div>

                <div style={{ width: '100%', marginTop: '16px' }}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.4 }}
                            style={{ width: '100%' }}
                        >
                            {renderContent()}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
}

export default PremierTeams;